import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import * as htmlToImage from 'html-to-image';

/**
 * Card header for every EMD chart/grid widget, following the app's standard
 * widget header (shared/charts/iconEvent-chart): title block on the left,
 * card-specific controls next to the icon rail on the right, with the app's
 * registered svg icons (export menu, help, fullscreen) so EMD cards read like
 * every other portal widget. iconEvent-chart itself cannot be reused here — it
 * DI-injects the whole shared chart ecosystem — hence this EMD-owned clone.
 *
 * Kept as dumb as the other EMD atoms: the only behaviour it owns is what all
 * cards want identically — a PNG snapshot of the enclosing card and the
 * fullscreen toggle. CSV is emitted so the grid card runs its own export.
 */
@Component({
    selector: 'emd-widget-header',
    template: `
    <div class="emd-wh">
      <div class="emd-wh-titles">
        <div class="emd-wh-title">{{ title }}</div>
        <div class="emd-wh-subtitle" *ngIf="subtitle">{{ subtitle }}</div>
      </div>

      <!-- Centre slot for the 3-up headers (title | pills | tools). -->
      <div class="emd-wh-center">
        <ng-content select="[emdHeaderCenter]"></ng-content>
      </div>

      <div class="emd-wh-tools">
        <!-- Qlik-style: the widget being selected in hosts its own confirm/cancel. -->
        <emd-selection-toolbar
          *ngIf="selectionOrigin"
          [origin]="selectionOrigin"
        ></emd-selection-toolbar>

        <ng-content></ng-content>

        <!-- Rail order: info, fullscreen, export. -->
        <span class="emd-wh-icons">
          <mat-icon
            *ngIf="infoText"
            matTooltip="Help"
            matTooltipClass="emd-tip-mat"
            class="emd-wh-icon"
            svgIcon="info_circle"
            [matMenuTriggerFor]="infoMenu"
          ></mat-icon>
          <mat-menu #infoMenu="matMenu">
            <div class="emd-wh-info" (click)="$event.stopPropagation()">
              <div class="emd-wh-info-title">{{ title }}</div>
              <div class="emd-wh-info-text">{{ infoText }}</div>
            </div>
          </mat-menu>

          <mat-icon
            *ngIf="expand"
            [matTooltip]="isFullscreen ? 'Exit Full Screen' : 'Show Full Screen'"
            matTooltipClass="emd-tip-mat"
            class="emd-wh-icon"
            [svgIcon]="isFullscreen ? 'collapse_arrow' : 'expand_circle'"
            (click)="toggleFullscreen()"
          ></mat-icon>

          <mat-icon
            *ngIf="png || csv"
            matTooltip="Export"
            matTooltipClass="emd-tip-mat"
            class="emd-wh-icon"
            svgIcon="dotmenu_circle"
            [matMenuTriggerFor]="exportMenu"
          ></mat-icon>
          <mat-menu #exportMenu="matMenu">
            <a mat-menu-item class="emd-wh-menu-item" *ngIf="csv" (click)="csvExport.emit()">
              <mat-icon svgIcon="csv"></mat-icon>Export as CSV
            </a>
            <a mat-menu-item class="emd-wh-menu-item" *ngIf="png" (click)="exportPng()">
              <mat-icon svgIcon="png"></mat-icon>Export as PNG
            </a>
          </mat-menu>
        </span>
      </div>
    </div>
  `,
    styleUrls: ['./emd-widget-header.component.scss'],
    standalone: false
})
export class EmdWidgetHeaderComponent implements OnDestroy {
  @Input() title = '';
  @Input() subtitle = '';
  /** Static help copy for the info menu; the icon is hidden while empty. */
  @Input() infoText = '';
  /**
   * Cross-filter id of this widget. When set, the header hosts the selection
   * toolbar, shown while a selection staged FROM this widget is pending.
   */
  @Input() selectionOrigin = '';
  /** Offer "Export as CSV" and emit csvExport — for the grid cards. */
  @Input() csv = false;
  /** Offer "Export as PNG" (snapshot of the enclosing .emd-card). */
  @Input() png = true;
  /** Show the fullscreen toggle. */
  @Input() expand = true;

  @Output() csvExport = new EventEmitter<void>();

  isFullscreen = false;

  /** Bound instance so add/removeEventListener refer to the same function. */
  private readonly onFsChange = () => {
    this.isFullscreen = document.fullscreenElement === this.card;
  };

  constructor(private readonly host: ElementRef<HTMLElement>) {
    document.addEventListener('fullscreenchange', this.onFsChange);
  }

  ngOnDestroy(): void {
    document.removeEventListener('fullscreenchange', this.onFsChange);
  }

  /** The card this header sits in — the subject of export and fullscreen. */
  private get card(): HTMLElement {
    return this.host.nativeElement.closest<HTMLElement>('.emd-card') ?? this.host.nativeElement;
  }

  exportPng(): void {
    htmlToImage
      .toPng(this.card, { backgroundColor: '#ffffff' })
      .then((dataUrl) => {
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `${(this.title || 'chart').replace(/[^\w-]+/g, '_')}_export.png`;
        a.click();
      })
      // html-to-image can reject on foreign stylesheets; the menu just closes.
      .catch(() => undefined);
  }

  toggleFullscreen(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      this.card.requestFullscreen?.();
    }
  }
}
